/* Global resets and keyframes, carried over from the design canvas. */

html,
body {
  margin: 0;
  background: #17181b;
  color: #e8e9ed;
  font-family: 'Schibsted Grotesk', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  overscroll-behavior: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* The board owns the viewport; screens that scroll opt in explicitly. */
html,
body,
#root {
  height: 100%;
}

body {
  overflow: hidden;
}

* {
  box-sizing: border-box;
}

a {
  color: #a78bfa;
}

a:hover {
  color: #c4b5fd;
}

::selection {
  background: rgba(167, 139, 250, 0.35);
}

button {
  font-family: inherit;
}

input,
textarea {
  font-family: inherit;
  user-select: text;
  -webkit-user-select: text;
}

/* Visible focus for keyboard users without ringing every mouse click. */
:focus-visible {
  outline: 2px solid #a78bfa;
  outline-offset: 2px;
}

/*
 * The canvas fills its region, so an outset ring would be clipped. Draw it
 * inside instead, and only for keyboard focus — clicking the canvas to work on
 * it should not light up a border.
 */
[role='application']:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px #a78bfa;
}

::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

::-webkit-scrollbar-thumb {
  background: #2e3038;
  border-radius: 5px;
  border: 2px solid #17181b;
}

::-webkit-scrollbar-track {
  background: transparent;
}

@keyframes sp-toast-in {
  from {
    opacity: 0;
    transform: translate(-50%, 8px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes sp-pop-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes sp-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes sp-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Respect a reduced-motion preference: keep the UI, drop the movement. */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}

/* Studio interface. Canvas coordinates remain in world-space inline styles. */
:root {
  --sp-bg: #17181b;
  --sp-panel: #23252c;
  --sp-raised: #2c2f38;
  --sp-line: #3c3f4a;
  --sp-muted: #b1b5c3;
  --sp-text: #e8e9ed;
  --sp-accent: #a78bfa;
  --sp-radius: 12px;
  --sp-shadow: 0 12px 36px #0005;
  color-scheme: dark;
}
body {
  font-size: 14px;
}
button,
input,
textarea,
select {
  font: inherit;
}
button {
  -webkit-tap-highlight-color: transparent;
}
button:disabled {
  cursor: default;
  opacity: 0.5;
}
button:focus-visible,
a:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 2px solid var(--sp-accent);
  outline-offset: 3px;
}
.sp-control {
  position: relative;
}
.sp-control:not(:disabled):active,
.sp-button:not(:disabled):active,
.sp-icon:not(:disabled):active {
  filter: brightness(0.92);
}
.sp-button,
.sp-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  padding: 8px 14px;
  border: 1px solid var(--sp-line);
  border-radius: 9px;
  background: var(--sp-panel);
  color: var(--sp-text);
  font-weight: 600;
  cursor: pointer;
  transition:
    background 160ms,
    border-color 160ms;
}
.sp-icon {
  width: 36px;
  padding: 6px;
  flex: none;
}
.sp-button:hover,
.sp-icon:hover {
  background: var(--sp-raised);
  border-color: #646777;
}
.sp-primary {
  background: var(--sp-accent);
  color: #16121f;
  border-color: transparent;
}
.sp-primary:hover {
  background: #c0a6ff;
}
.sp-danger {
  color: #ffb4b4;
  border-color: #ad5555;
  background: #41282c;
}
.sp-button[aria-pressed='true'],
.sp-icon[aria-pressed='true'] {
  color: var(--sp-accent);
  border-color: var(--sp-accent);
  background: #a78bfa18;
}
.sp-description {
  color: var(--sp-muted);
  line-height: 1.65;
  margin: 0 0 20px;
}
.sp-error {
  color: #ffb4b4;
  line-height: 1.5;
}
.sp-backdrop {
  position: fixed;
  inset: 0;
  background: #090a0f99;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.sp-dialog {
  width: min(460px, 100%);
  max-height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--sp-line);
  border-radius: 16px;
  background: var(--sp-panel);
  box-shadow: 0 24px 80px #0008;
  animation: sp-pop-in 180ms ease;
}
.sp-dialog-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--sp-line);
  flex: none;
}
.sp-dialog-heading h2 {
  margin: 0;
  font-size: 17px;
  font-weight: 650;
}
.sp-dialog-body {
  overflow: auto;
  padding: 20px;
}
.sp-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.sp-drawer {
  margin-left: auto;
  align-self: stretch;
  width: 360px;
  max-height: 100%;
}
.sp-notification {
  position: fixed;
  z-index: 30000;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px 10px 18px;
  border: 1px solid var(--sp-line);
  background: var(--sp-raised);
  border-radius: 12px;
  box-shadow: var(--sp-shadow);
  font-size: 13px;
  line-height: 1.5;
  animation: sp-toast-in 160ms ease;
}
.sp-notification-error {
  border-color: #ad5555;
}
.sp-notification > span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.sp-skeleton {
  background: linear-gradient(100deg, #23252c, #30333d, #23252c);
  background-size: 200% 100%;
  border-radius: 12px;
  min-height: 200px;
  animation: sp-loading 1.5s ease infinite;
}
@keyframes sp-loading {
  to {
    background-position: -200% 0;
  }
}
.sp-screen {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sp-page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 64px;
  padding: 0 28px;
  border-bottom: 1px solid #ffffff12;
  flex: none;
}
.sp-wordmark {
  font-size: 17px;
  font-weight: 750;
  letter-spacing: -0.03em;
}
.sp-brand-mark {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: var(--sp-accent);
  display: grid;
  place-items: center;
  color: #17121f;
  font-size: 18px;
  font-weight: 800;
}
.sp-page-body {
  overflow: auto;
  flex: 1;
}
.sp-gallery {
  max-width: 1160px;
  margin: auto;
  padding: 48px 32px 80px;
}
.sp-gallery-heading {
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 28px;
}
.sp-gallery-heading h1 {
  margin: 0 0 8px;
  font-size: 30px;
  letter-spacing: -0.035em;
}
.sp-gallery-heading p {
  color: var(--sp-muted);
  margin: 0;
  line-height: 1.6;
}
.sp-gallery-actions {
  display: flex;
  gap: 8px;
  flex: none;
}
.sp-board-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr));
  gap: 22px;
}
.sp-board-card {
  position: relative;
  border: 1px solid #ffffff14;
  border-radius: 16px;
  background: #202228;
  transition: border-color 160ms;
  min-width: 0;
}
.sp-board-card:hover,
.sp-board-card:focus-within {
  border-color: #777186;
}
.sp-board-card > button:first-child {
  border-radius: 16px;
  overflow: hidden;
}
.sp-card-menu {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}
.sp-menu {
  min-width: 190px;
  padding: 6px;
  background: var(--sp-panel);
  border: 1px solid var(--sp-line);
  border-radius: 12px;
  box-shadow: var(--sp-shadow);
}
.sp-menu button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
  width: 100%;
  border: 0;
  border-radius: 7px;
  padding: 10px 12px;
  background: transparent;
  color: var(--sp-text);
  cursor: pointer;
}
.sp-menu button:hover,
.sp-menu button:focus-visible {
  background: var(--sp-raised);
}
.sp-menu .sp-danger-text {
  color: #ffb4b4;
}
.sp-empty {
  padding: 48px 24px;
  border: 1px dashed var(--sp-line);
  border-radius: 16px;
  text-align: center;
}
.sp-empty h2 {
  font-size: 20px;
  margin: 0 0 10px;
}
.sp-empty p {
  color: var(--sp-muted);
  line-height: 1.6;
}
.sp-board-shell {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  user-select: none;
}
.sp-board-topbar {
  height: 60px;
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  background: var(--sp-bg);
  border-bottom: 1px solid #ffffff12;
  z-index: 70;
}
.sp-board-title {
  width: clamp(140px, 20vw, 280px);
  min-width: 0;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  color: var(--sp-text);
  font-weight: 650;
  padding: 8px;
  text-overflow: ellipsis;
}
.sp-board-title:hover {
  border-color: var(--sp-line);
}
.sp-board-status {
  font-size: 12px;
  color: var(--sp-muted);
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}
.sp-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #83d7b2;
  flex: none;
}
.sp-topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.sp-history {
  display: flex;
  gap: 2px;
  border-left: 1px solid var(--sp-line);
  padding-left: 8px;
}
.sp-board-search {
  position: relative;
}
.sp-board-search input {
  width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--sp-line);
  background: #202228;
  color: var(--sp-text);
  border-radius: 9px;
}
.sp-board-search-results {
  position: absolute;
  right: 0;
  top: 42px;
  width: 280px;
  max-height: 300px;
  overflow: auto;
}
.sp-board-row {
  flex: 1;
  display: flex;
  min-height: 0;
}
.sp-canvas-region {
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  display: flex;
  overflow: hidden;
}
.sp-selection-toolbar {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px;
  border: 1px solid var(--sp-line);
  border-radius: 12px;
  box-shadow: var(--sp-shadow);
  background: var(--sp-panel);
  max-width: calc(100% - 32px);
  z-index: 65;
}
.sp-selection-toolbar .sp-selection-count {
  font-size: 12px;
  color: var(--sp-muted);
  padding: 0 6px;
  white-space: nowrap;
}
.sp-tool-hint {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 14px;
  background: var(--sp-panel);
  border: 1px solid var(--sp-line);
  border-radius: 9px;
  font-size: 12px;
  max-width: calc(100% - 32px);
  text-align: center;
  z-index: 60;
  pointer-events: none;
}
.sp-demo-banner {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 10px 8px 16px;
  background: var(--sp-panel);
  border: 1px solid #a78bfa55;
  border-radius: 12px;
  box-shadow: var(--sp-shadow);
  z-index: 60;
  max-width: calc(100% - 32px);
}
.sp-demo-copy {
  min-width: 0;
  font-size: 12px;
  color: var(--sp-muted);
  line-height: 1.5;
}
.sp-demo-copy strong {
  display: block;
  color: var(--sp-text);
  font-size: 13px;
}
.sp-demo-banner button {
  flex: none;
}
.sp-tool-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.sp-tool-options button {
  min-height: 64px;
  flex-direction: column;
}
.sp-editor-drawer-content > div,
.sp-editor-drawer-content > aside {
  position: static !important;
  width: 100% !important;
  max-width: 100% !important;
  border: 0 !important;
  box-shadow: none !important;
  max-height: none !important;
}
.sp-editor-drawer-content {
  min-width: 0;
}
.sp-mobile-dock {
  display: none;
}
.sp-inline-mobile {
  position: fixed;
  z-index: 21000;
  left: 12px;
  right: 12px;
  top: 80px;
  background: var(--sp-panel);
  border: 1px solid var(--sp-line);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--sp-shadow);
}
.sp-inline-mobile textarea,
.sp-inline-mobile input {
  width: 100%;
  font-size: 16px;
  padding: 12px;
  background: #1b1d23;
  color: var(--sp-text);
  border: 1px solid var(--sp-line);
  border-radius: 8px;
  margin-bottom: 10px;
}
.sp-inline-mobile textarea {
  min-height: 130px;
  resize: vertical;
}
.sp-upload-status {
  position: absolute;
  left: 50%;
  bottom: 70px;
  transform: translateX(-50%);
  background: var(--sp-panel);
  padding: 10px 16px;
  border: 1px solid var(--sp-line);
  border-radius: 10px;
  z-index: 60;
}
.sp-onboarding {
  max-width: calc(100% - 90px);
}
.sp-onboarding-toggle {
  background: none;
  border: 0;
  color: var(--sp-text);
  text-align: left;
  cursor: pointer;
  padding: 0;
  font-weight: 600;
}
.sp-landing-hero {
  max-width: 860px;
  margin: auto;
  padding: 88px 24px 0;
  text-align: center;
}
.sp-landing-hero h1 {
  font-size: clamp(42px, 6vw, 76px);
  line-height: 1.04;
  letter-spacing: -0.05em;
  margin: 24px 0;
}
.sp-demo-preview {
  border: 1px solid var(--sp-line);
  border-radius: 18px;
  background: #1b1d22;
  padding: 20px;
  box-shadow: 0 20px 80px #0003;
}
.sp-demo-preview svg {
  width: 100%;
  height: auto;
  display: block;
}
.sp-form-shell input {
  min-width: 0;
}
@media (hover: hover) {
  .sp-control[data-tooltip]:focus-visible::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 10px;
    background: #353844;
    border: 1px solid var(--sp-line);
    color: var(--sp-text);
    border-radius: 6px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 100;
    pointer-events: none;
  }
}
@media (max-width: 1199px) {
  .sp-board-topbar {
    padding: 0 10px;
    gap: 6px;
  }
  .sp-board-status {
    max-width: 150px;
    overflow: hidden;
  }
  .sp-board-title {
    flex: 1;
    width: 140px;
  }
  .sp-board-search input {
    width: 150px;
  }
}
@media (max-width: 767px) {
  .sp-page-header {
    padding: 0 16px;
    height: 58px;
  }
  .sp-gallery {
    padding: 28px 16px 48px;
  }
  .sp-gallery-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 20px;
  }
  .sp-gallery-actions {
    width: 100%;
  }
  .sp-gallery-actions button {
    flex: 1;
  }
  .sp-board-topbar {
    height: 54px;
  }
  .sp-board-title {
    font-size: 14px;
  }
  .sp-board-status {
    display: none;
  }
  .sp-topbar-actions {
    margin-left: 0;
  }
  .sp-history {
    border: 0;
    padding: 0;
  }
  .sp-desktop-only {
    display: none !important;
  }
  .sp-backdrop {
    padding: 12px;
    align-items: flex-end;
  }
  .sp-dialog {
    max-height: calc(var(--sp-visual-height, 100dvh) - 24px);
    width: 100%;
    border-radius: 18px;
  }
  .sp-drawer {
    align-self: flex-end;
    margin: 0;
    max-height: 76dvh;
  }
  .sp-dialog-body {
    padding: 16px 16px max(16px, env(safe-area-inset-bottom));
  }
  .sp-mobile-dock {
    display: flex;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 70;
    align-items: center;
    justify-content: space-around;
    padding: 8px 8px max(8px, env(safe-area-inset-bottom));
    background: var(--sp-panel);
    border-top: 1px solid var(--sp-line);
    gap: 4px;
  }
  .sp-mobile-dock button {
    min-width: 48px;
    min-height: 48px;
    flex-direction: column;
    gap: 3px;
    font-size: 11px;
    background: transparent;
    border-color: transparent;
    padding: 5px 10px;
  }
  .sp-mobile-dock button[aria-pressed='true'] {
    background: #a78bfa22;
    border-color: #a78bfa66;
  }
  .sp-selection-toolbar {
    top: auto;
    bottom: 126px;
    left: 12px;
    right: 12px;
    transform: none;
    max-width: none;
    justify-content: space-between;
  }
  .sp-demo-banner {
    top: 10px;
    left: 10px;
    right: 10px;
    transform: none;
    max-width: none;
    padding: 8px 10px;
    gap: 8px;
  }
  .sp-demo-copy {
    font-size: 11px;
    flex: 1;
  }
  .sp-demo-copy strong {
    font-size: 12px;
  }
  .sp-demo-copy .sp-demo-detail {
    display: none;
  }
  .sp-demo-banner button {
    font-size: 11px;
    padding: 6px 9px;
  }
  .sp-notification {
    bottom: calc(80px + env(safe-area-inset-bottom));
    width: max-content;
    max-width: calc(100vw - 24px);
  }
  .sp-tool-hint {
    top: 78px;
    font-size: 12px;
    width: max-content;
  }
  .sp-minimap {
    display: none !important;
  }
  .sp-zoom-bar {
    bottom: calc(76px + env(safe-area-inset-bottom)) !important;
    right: 10px !important;
  }
  .sp-onboarding {
    left: 10px !important;
    bottom: calc(126px + env(safe-area-inset-bottom)) !important;
    width: 220px !important;
    max-width: calc(100% - 20px);
  }
  .sp-shortcuts {
    right: 10px !important;
    bottom: 122px !important;
    max-height: calc(100% - 210px) !important;
  }
  .sp-upload-status {
    bottom: 144px;
  }
  .sp-landing-hero {
    padding-top: 48px;
  }
}
@media (pointer: coarse) {
  .sp-control,
  .sp-button,
  .sp-icon,
  .sp-menu button {
    min-height: 44px;
  }
  .sp-icon {
    min-width: 44px;
  }
  .sp-input {
    min-height: 44px;
    font-size: 16px !important;
  }
  .sp-control[aria-label^='Accent'] {
    min-width: 44px;
  }
  [data-resize] {
    min-width: 24px;
    min-height: 24px;
  }
}
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation: none !important;
    transition: none !important;
  }
}
.sp-mobile-editor label {
  display: block;
  color: var(--sp-muted);
  margin-bottom: 14px;
}
.sp-mobile-editor input,
.sp-mobile-editor textarea {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 12px;
  font-size: 16px;
  color: var(--sp-text);
  background: var(--sp-bg);
  border: 1px solid var(--sp-line);
  border-radius: 8px;
}
.sp-mobile-editor textarea {
  min-height: 120px;
  max-height: 35dvh;
  resize: vertical;
}
.sp-selection-toolbar [role='button'] {
  white-space: nowrap;
}
.sp-selection-toolbar [role='button'] > span:last-child {
  display: none;
}
.sp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  width: 100%;
  color: var(--sp-muted);
  font-size: 12px;
}
.sp-field > span {
  font-weight: 550;
}
.sp-landing header {
  flex-wrap: wrap;
  height: auto !important;
  min-height: 56px;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}
@media (max-width: 767px) {
  .sp-landing header > div:nth-child(3) {
    display: none;
  }
  .sp-demo-preview {
    padding: 12px;
  }
  .sp-demo-preview > div {
    font-size: 10px !important;
    flex-wrap: wrap;
  }
}
@media (pointer: coarse) {
  [role='menuitem'] {
    min-height: 44px;
  }
  [role='menuitem'][aria-label^='Sticky colour'] {
    min-width: 44px;
  }
  .sp-editor-drawer-content input {
    min-height: 44px;
    font-size: 16px !important;
  }
}

/* Shared button variants also support existing callers with dynamic styles. */
.sp-control-primary,
.sp-control-danger,
.sp-control-quiet {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  padding: 8px 14px;
  border: 1px solid var(--sp-line);
  border-radius: 9px;
  font-weight: 600;
}
.sp-control-primary {
  background: var(--sp-accent);
  color: #16121f;
  border-color: transparent;
}
.sp-control-danger {
  background: #41282c;
  color: #ffb4b4;
  border-color: #ad5555;
}
.sp-control-quiet {
  background: transparent;
  color: var(--sp-text);
}
.sp-control[aria-busy='true']::before {
  content: '';
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: sp-spin 0.7s linear infinite;
}

/* Screen-space typography controls stay readable at every canvas zoom. */
.sp-text-formatting {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: var(--sp-panel);
  color: var(--sp-text);
  border: 1px solid var(--sp-line);
  border-radius: 12px;
}
.sp-text-formatting-floating {
  position: fixed;
  z-index: 1000;
  max-width: calc(100vw - 24px);
  box-shadow: var(--sp-shadow);
}
.sp-text-formatting label {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
}
.sp-format-scope {
  font-size: 12px;
  width: 100%;
  color: var(--sp-muted);
}
.sp-text-formatting select,
.sp-text-formatting input {
  height: 34px;
  min-width: 0;
  padding: 4px 6px;
  border: 1px solid var(--sp-line);
  border-radius: 6px;
  color: inherit;
  background: var(--sp-bg);
  font: inherit;
}
.sp-text-formatting input[type='number'] {
  width: 64px;
}
.sp-text-formatting input[type='color'] {
  width: 40px;
  padding: 3px;
  cursor: pointer;
}
.sp-format-buttons {
  display: flex;
  gap: 3px;
  align-self: flex-end;
}
.sp-format-bold {
  font-weight: 700;
}
.sp-format-italic {
  font-style: italic;
}
.sp-format-underline {
  text-decoration: underline;
}
.sp-text-formatting button[aria-pressed='true'] {
  background: var(--sp-accent);
  color: #17151e;
}
@media (pointer: coarse) {
  .sp-text-formatting select,
  .sp-text-formatting input,
  .sp-text-formatting button {
    min-height: 44px;
    min-width: 44px;
  }
}
